<template>
    <div class="md-layout">
        <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
            <div class="custom-toolbar">
                <div class="custom-toolbar-start">
                    <router-link
                        :to="{
                            name: 'Service Areas'
                        }"
                    >
                        <h3 class="title">Service Areas</h3>
                    </router-link>
                    <h3 class="title blockout-day-crumb-title">
                        &nbsp;&nbsp; > &nbsp;&nbsp; {{ blockoutDayData.serviceAreaName }} &nbsp;&nbsp; > &nbsp;&nbsp;
                        Blockout Days
                    </h3>
                </div>
            </div>
        </div>
        <div class="md-layout-item md-size-100">
            <md-card>
                <md-card-header class="md-card-header-icon md-card-header-black">
                    <div class="card-icon">
                        <md-icon>my_location</md-icon>
                    </div>
                </md-card-header>
                <md-card-content>
                    <div class="blockout-day-page-title">
                        Blockout Days
                    </div>
                    <div>
                        Blockout specific days(s) where delivery is not available, for example Christmas Day or other
                        public holidays.
                        <br />
                        To blockout a single day, set the start and end dates to the same date.
                    </div>
                    <service-areas-blockout-day :service-area-id="serviceAreaId" :blockout-day-data="blockoutDayData" />
                    <div class="custom-toolbar custom-blockout-day-toolbar">
                        <div class="custom-toolbar-start">
                            <md-button
                                class="md-button md-primary custom-back-button"
                                @click="handleBackToServiceAreas"
                            >
                                Back
                            </md-button>
                        </div>
                        <div class="custom-toolbar-end">
                            <md-button class="md-button md-primary custom-save-button" @click="handleSaveBlockoutDays">
                                Save
                            </md-button>
                        </div>
                    </div>
                </md-card-content>
            </md-card>
        </div>
    </div>
</template>

<script>
import { handleRequests, showErrorMessage, rowStatus } from '@/helpers';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import ServiceAreasBlockoutDay from './ServiceAreasBlockoutDay';

export default {
    name: 'ServiceAreasBlockoutDayPage',
    mixins: [GeneralMixin],
    components: {
        ServiceAreasBlockoutDay
    },
    data() {
        return {
            serviceAreaId: null,
            blockoutDayData: {
                serviceAreaName: null,
                blockoutDays: []
            },
            previousBlockoutDays: null,
            rowStateFields: ['blockoutDayName', 'startDate', 'endDate'],
            isDataChanged: false
        };
    },
    async mounted() {
        this.serviceAreaId = this.$route.params.serviceAreaId;
        await this.getBlockoutDays();
    },
    watch: {
        'blockoutDayData.blockoutDays': {
            deep: true,
            handler(newBlockoutDays) {
                if (JSON.stringify(newBlockoutDays) === this.previousBlockoutDays) {
                    return;
                }

                const previousBlockoutDays = JSON.parse(this.previousBlockoutDays);
                newBlockoutDays.forEach((item) => {
                    if (item.blockoutDayId !== null && ![rowStatus.DELETE, rowStatus.ADD].includes(item.rowStatus)) {
                        const previousDeliveryWindow = previousBlockoutDays.find((x) => {
                            return x.blockoutDayId === item.blockoutDayId;
                        });
                        const rowChanged = this.rowStateFields.some((x) => item[x] !== previousDeliveryWindow[x]);
                        if (rowChanged) {
                            this.isDataChanged = true;
                            item.rowStatus = rowStatus.EDIT;
                        }
                    }

                    if ([rowStatus.DELETE, rowStatus.ADD].includes(item.rowStatus) && !this.isDataChanged) {
                        this.isDataChanged = true;
                    }
                });

                this.previousBlockoutDays = JSON.stringify(this.blockoutDayData.blockoutDays);
            }
        }
    },
    methods: {
        async getBlockoutDays() {
            this.$_handleLoaderState(true);
            try {
                const api = `/api/teams/service-areas/${this.serviceAreaId}/blockout-days`;
                const response = await handleRequests(api);
                this.blockoutDayData = {
                    serviceAreaName: null,
                    blockoutDays: []
                };
                await this.$nextTick(() => {
                    this.blockoutDayData = response.data;
                    this.previousBlockoutDays = JSON.stringify(this.blockoutDayData.blockoutDays);
                });
                this.isDataChanged = false;
                this.$_handleLoaderState(false);
            } catch (e) {
                this.$_handleLoaderState(false);
                const message = 'Cannot retrieve blockout days.';
                showErrorMessage(this, message, e);
            }
        },
        blockoutDayDataIsValid() {
            const { blockoutDays } = this.blockoutDayData;
            const filteredBlockoutDays = blockoutDays.filter((x) => x.rowStatus !== rowStatus.DELETE);
            const missingFields = [];

            if (filteredBlockoutDays.some((x) => !x.blockoutDayName)) {
                missingFields.push('Name');
            }

            if (filteredBlockoutDays.some((x) => x.startDate === null)) {
                missingFields.push('Start Date');
            }

            if (filteredBlockoutDays.some((x) => x.endDate === null)) {
                missingFields.push('End Date');
            }

            if (missingFields.length > 0) {
                showErrorMessage(
                    this,
                    `${missingFields.join(', ')} ${missingFields.length === 1 ? 'is' : 'are'} required.`
                );
                return false;
            }

            if (filteredBlockoutDays.some((x) => !this.$_isValidDateRange(x.startDate, x.endDate))) {
                showErrorMessage(this, 'Start and End Date range is invalid.');
                return false;
            }

            return true;
        },
        async saveBlockoutDays() {
            this.$_handleLoaderState(true, 'SAVING...');
            const payload = {
                method: 'post',
                data: {
                    serviceAreaId: parseInt(this.serviceAreaId, 10),
                    blockoutDays: this.getChangedRows()
                }
            };
            const api = '/api/teams/service-areas/blockout-days';
            try {
                await handleRequests(api, payload);
                this.$notify({
                    message: 'Blockout days saved!',
                    type: 'success'
                });

                this.isDataChanged = false;
                this.$_handleLoaderState(false);

                await this.getBlockoutDays();
            } catch (e) {
                this.$_handleLoaderState(false);
                const message = 'Cannot save blockout days.';
                showErrorMessage(this, message, e);
            }
        },
        checkForUnsavedChanges() {
            return new Promise((resolve, reject) => {
                if (this.isDataChanged) {
                    this.$messageBox
                        .show({
                            class: 'sm-modal-container',
                            title: 'Save blockout days',
                            body: 'You have unsaved changes. Would you like to save the blockout days?',
                            buttons: ['Yes', 'No']
                        })
                        .then(async (response) => {
                            if (response.toLowerCase() === 'yes') {
                                await this.handleSaveBlockoutDays();
                            }

                            resolve();
                        });
                } else {
                    resolve();
                }
            });
        },
        getChangedRows() {
            return this.blockoutDayData.blockoutDays.filter((x) =>
                [rowStatus.ADD, rowStatus.EDIT, rowStatus.DELETE].includes(x.rowStatus)
            );
        },
        async handleSaveBlockoutDays() {
            if (this.blockoutDayDataIsValid()) {
                await this.saveBlockoutDays();
            }
        },
        async handleBackToServiceAreas() {
            try {
                this.checkForUnsavedChanges(true).then(async () => {
                    this.$router.push({ name: 'Service Areas' });
                });
            } catch (ex) {
                this.$notify({
                    message: ex.message,
                    type: 'danger'
                });
            }
        }
    }
};
</script>
<style lang="scss" scoped>
.card-icon {
    background-color: #384553 !important;
}

.custom-save-button,
.custom-back-button {
    width: 150px;
}

.custom-save-button {
    margin-right: 0;
}

.custom-back-button,
.custom-back-button:hover {
    background-color: #999999 !important;
    margin-left: 0;
}

.custom-blockout-day-toolbar {
    border-top: 1px solid #dddddd;
    padding-top: 10px !important;
    margin-top: 30px !important;
}

.blockout-day-page-title {
    font-weight: bold;
    font-size: 24px;
    margin-top: 20px;
    margin-bottom: 5px;
}

.blockout-day-crumb-title {
    font-weight: bold;
}
</style>
